'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AiDisclaimerBanner } from './ui/AiDisclaimerBanner';
import { ChatMessageList } from './ui/ChatMessageList';
import { ChatInputForm } from './ui/ChatInputForm';
import { DeepSeekActiveBanner } from './ui/composer/DeepSeekActiveBanner';
import { ChatStreamingIndicator } from './ui/ChatStreamingIndicator';
import { ChatSuggestedMessages } from './ui/ChatSuggestedMessages';
import { ChatErrorRetry } from './ui/ChatErrorRetry';
import { ChatTokenUsageTrigger } from './ui/ChatTokenUsageTrigger';
import { ChatTokenUsageModal } from './ChatTokenUsageModal';
import { ChatSkillsModal } from './ChatSkillsModal';
import { useChatState } from './hooks/useChatState';
import { useChatEffects } from './hooks/useChatEffects';
import { useChatDerivedState } from './hooks/useChatDerivedState';
import { useChatHandlers } from './hooks/useChatHandlers';
import { usePreviewData } from './hooks/usePreviewData';
import { computeOlderMessagesToPrepend } from './utils/olderMessages';
import { isErrorStatus } from './utils/chatStatus';
import { CHAT_LAYOUT } from '../styles/chat-layout';
import type { AttachedSkill } from './skills/types';
import type { FilePart } from '../lib/db/types';
import type { ChatRunStatus } from '../contexts/ChatActivityContext';

/** Virtuoso `firstItemIndex` base. Prepending older messages decrements it so
 *  the viewport stays anchored (documented Virtuoso reverse-scroll contract). */
const VIRTUOSO_START_INDEX = 1_000_000;

/**
 * Chat Interface using useChat hook with SDK tool approval
 *
 * Tool approval flow (AI SDK):
 * - When a tool needs approval, stream emits tool-approval-request → part.state === 'approval-requested', part.approval.id
 * - UI shows Allow/Deny; user calls addToolApprovalResponse({ id: part.approval.id, approved, reason })
 * - sendAutomaticallyWhen uses hasApprovedToolCallReadyToSend() to detect when to auto-send
 * - Request is sent automatically; server runs the tool and streams the follow-up response
 *
 * Stream Cleanup:
 * - Aborts active streams when chatId changes (user switches chats)
 * - Cleans up on component unmount (user navigates away)
 */
interface ChatInterfaceProps {
  chatId: string;
  apiBaseUrl?: string;
  onMessageSent?: () => void;
  initialQuery?: string;
  /**
   * Files uploaded from the start screen before this chat existed. Sent with the
   * first message so attaching a file pre-chat behaves like attaching one in an
   * existing conversation.
   */
  initialFiles?: FilePart[];
  /** Skill attached on the start screen; seeds the composer badge + first send. */
  initialSkill?: AttachedSkill | null;
  chatTitle?: string;
  showSuggestedMessages?: boolean;
  locale?: string;
  onLoadOlderMessages?: () => Promise<void>;
  hasMoreMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  initialMessages?: React.ComponentProps<typeof ChatMessageList>['messages'];
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  /** MCP connections page — surfaced in the composer's "+" menu when provided. */
  mcpConnectionsHref?: string;
  /**
   * Reports whether this chat is mid-turn. Lets a chat that is NOT on screen
   * (a background tab, a sidebar row) still show a live indicator — this
   * component is the only place that knows, since it owns the `useChat`.
   */
  onStatusChange?: (status: ChatRunStatus) => void;
}

export function ChatInterface({
  chatId,
  apiBaseUrl = '/api/meridian',
  onMessageSent,
  initialQuery = '',
  initialFiles,
  initialSkill = null,
  chatTitle = '',
  showSuggestedMessages = false,
  locale = 'en',
  onLoadOlderMessages,
  hasMoreMessages = false,
  isLoadingOlderMessages = false,
  initialMessages = [],
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  mcpConnectionsHref,
  onStatusChange,
}: ChatInterfaceProps) {
  const [attachedSkill, setAttachedSkill] = useState<AttachedSkill | null>(initialSkill);

  // Initialize chat state
  const {
    messages,
    setMessages,
    sendMessage,
    addToolApprovalResponse,
    status,
    stop,
    error,
    regenerate,
    input,
    setInput,
    inputRef,
    scrollContainerRef,
    isStreaming,
    isBusy,
    sendMessageRef,
    messagesLengthRef,
  } = useChatState({
    chatId,
    apiBaseUrl,
    initialMessages,
    onMessageSent,
  });

  // Calculate derived state (must precede effects/handlers that depend on it)
  const isWaiting = status === 'submitted' || status === 'streaming';

  // Publish run status for the tab/sidebar live indicators. Derived from the
  // same `status` the UI already renders, so the dot can never disagree with
  // what the conversation is doing.
  useEffect(() => {
    if (!onStatusChange) return;
    onStatusChange(isWaiting ? 'streaming' : isErrorStatus(status) ? 'error' : 'idle');
  }, [isWaiting, status, onStatusChange]);
  const { pendingToolCallConfirmation, toolProgress, showWorkingShimmer, suggestedMessages } =
    useChatDerivedState({
      messages,
      isWaiting,
      showSuggestedMessages,
    });

  // Setup effects
  useChatEffects({
    inputRef,
    status,
    initialQuery,
    initialFiles,
    initialActiveSkillId: initialSkill?.id ?? null,
    chatId,
    sendMessage,
    messages,
    sendMessageRef,
    messagesLengthRef,
    pendingToolCallConfirmation,
  });

  // Reverse pagination: `useChat` seeds from initialMessages only at mount, so
  // older history fetched later must be merged into the live list. Prepend any
  // server messages not already present (dedup by id — safe against page
  // ordering) and decrement Virtuoso's firstItemIndex to hold the scroll anchor.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUOSO_START_INDEX);
  useEffect(() => {
    const toPrepend = computeOlderMessagesToPrepend(initialMessages, messagesRef.current);
    if (toPrepend.length === 0) return;
    setMessages((prev) => [...toPrepend, ...prev]);
    setFirstItemIndex((prev) => prev - toPrepend.length);
    // Only react to server-history changes, not to every streaming token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessages]);

  // Setup event handlers
  const { handleSubmit, handleKeyDown, handleToolApprovalResponse, handleSuggestionClick } =
    useChatHandlers({
      input,
      isBusy,
      pendingToolCallConfirmation,
      sendMessage,
      setInput,
      addToolApprovalResponse,
      activeSkillId: attachedSkill?.id ?? null,
    });

  const handleAttachSkill = useCallback((skill: AttachedSkill) => {
    setAttachedSkill(skill);
  }, []);

  const handleClearAttachedSkill = useCallback(() => {
    setAttachedSkill(null);
  }, []);

  // Stable no-op so the memoized ChatInputForm doesn't re-render on every parent
  // render (MEMO_STABLE_PROPS). Files are included in the message parts when sent
  // via handleSubmit.
  const handleFilesSelected = useCallback(() => {}, []);

  // Setup preview data fetching
  const { previewData, previewLoading, previewError, fetchPreviewData } = usePreviewData();

  // Stable approve/deny handlers so the memoized ChatMessageItem rows can bail
  // out during streaming instead of re-rendering on every new inline closure.
  const handleApprove = useCallback(
    (id: string) => handleToolApprovalResponse(id, true),
    [handleToolApprovalResponse]
  );
  const handleDeny = useCallback(
    (id: string) => handleToolApprovalResponse(id, false, 'User denied'),
    [handleToolApprovalResponse]
  );

  return (
    <div className={CHAT_LAYOUT.chatInterface}>
      {/* Message List - grows to fill available space */}
      <div className={CHAT_LAYOUT.messageListContainer}>
        <ChatMessageList
          messages={messages}
          scrollContainerRef={scrollContainerRef}
          botAvatarUrl={botAvatarUrl}
          userInitials={userInitials}
          userAvatarUrl={userAvatarUrl}
          previewData={previewData}
          previewLoading={previewLoading}
          previewError={previewError}
          onFetchPreview={fetchPreviewData}
          onApprove={handleApprove}
          onDeny={handleDeny}
          isBusy={isBusy}
          apiBaseUrl={apiBaseUrl}
          firstItemIndex={firstItemIndex}
          hasMoreMessages={hasMoreMessages}
          isLoadingOlderMessages={isLoadingOlderMessages}
          onLoadOlderMessages={onLoadOlderMessages}
        />
      </div>

      {/* Streaming Indicator */}
      <ChatStreamingIndicator
        toolProgress={toolProgress}
        showWorkingShimmer={showWorkingShimmer && messages.length > 0}
        isStreaming={isStreaming}
        botAvatarUrl={botAvatarUrl}
      />

      {/* Inline error + Retry when the last turn failed. `regenerate` re-runs the
          last user turn and preserves chat state. */}
      {isErrorStatus(status) && <ChatErrorRetry error={error} onRetry={() => regenerate()} />}

      {/* Suggested Messages */}
      <ChatSuggestedMessages
        suggestions={suggestedMessages}
        onSuggestionClick={handleSuggestionClick}
      />

      {/* Input Section */}
      <div className={CHAT_LAYOUT.inputSection}>
        <div className={CHAT_LAYOUT.inputWrapper}>
          <AiDisclaimerBanner />
          <div className="flex justify-end -mt-0.5 mb-1">
            <ChatTokenUsageTrigger chatId={chatId} apiBaseUrl={apiBaseUrl} />
          </div>
          <DeepSeekActiveBanner apiBaseUrl={apiBaseUrl} />
          <ChatInputForm
            input={input}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            disabled={isBusy || pendingToolCallConfirmation}
            isBusy={isBusy}
            onStop={stop}
            inputRef={inputRef}
            chatId={chatId}
            apiBaseUrl={apiBaseUrl}
            attachedSkill={attachedSkill}
            onAttachSkill={handleAttachSkill}
            onClearAttachedSkill={handleClearAttachedSkill}
            onFilesSelected={handleFilesSelected}
            mcpConnectionsHref={mcpConnectionsHref}
          />
        </div>
      </div>
      <ChatTokenUsageModal chatId={chatId} apiBaseUrl={apiBaseUrl} />
      <ChatSkillsModal apiBaseUrl={apiBaseUrl} onSelectSkill={handleAttachSkill} />
    </div>
  );
}
